import {
	Component,
	ElementRef,
	EventEmitter,
	HostListener,
	Input,
	Output,
	ViewChild,
	ViewEncapsulation,
} from '@angular/core';
import { FreudMenuItem, FreudMessageService } from '@freud-ds/web-components';
import { TranslateService } from '@ngx-translate/core';
import { ESidebarMode } from 'app/enums/sidebarmode.enum';
import * as IAction from 'app/interfaces/actiontracker.interface';
import { FeedNotification } from 'app/interfaces/feed-notifications.interface';
import { ActionTrackerService } from 'app/services/actiontracker.service';
import { DateTimeService } from 'app/services/datetime.service';
import { FeedNotificationService } from 'app/services/feed-notifications.service';
import { AppointmentSidebarService } from 'modules/appointments/services/appointment-sidebar.service';
import moment from 'moment';

@Component({
	selector: 'notifications-list',
	templateUrl: './notifications-list.component.html',
	styleUrls: ['./notifications-list.component.scss'],
	providers: [FreudMessageService],
	encapsulation: ViewEncapsulation.None,
})
export class NotificationsListComponent {
	@Input()
	public notifications: FeedNotification[];

	@ViewChild('hideNotification', { read: ElementRef })
	protected hideNotificationElement: ElementRef;

	@Output()
	protected onNotificationHidden: EventEmitter<number> = new EventEmitter();

	private notificationToBeHidden: number;

	private lockInteraction = false;

	protected menuActions: FreudMenuItem[] = [
		{
			label: this.translateService.instant('NOTIFICATIONS.CARD.HIDE.TEXT'),
			command: () =>
				this.setNotificationAsHidden(this.getNotificationToBeHidden()),
		},
	];

	constructor(
		private translateService: TranslateService,
		private dateTimeService: DateTimeService,
		private feedNotificationService: FeedNotificationService,
		private messageService: FreudMessageService,
		private actionTrackerService: ActionTrackerService,
		private sidebarService: AppointmentSidebarService
	) {}

	setNotificationToBeHidden(notificationId: number): void {
		this.notificationToBeHidden = notificationId;
	}

	protected getNotificationToBeHidden(): number {
		return this.notificationToBeHidden;
	}

	protected interactWithNotification(notification: FeedNotification): void {
		if (this.lockInteraction) {
			return;
		}

		if (!notification.interacted) {
			this.feedNotificationService
				.setNotificationAsInteracted(notification.id)
				.subscribe(() => (notification.interacted = true));
			this.trackInteraction(notification);
		}

		if (notification.openType.web === 'SIDEBAR') {
			this.openSidebar(notification);
			return;
		}

		this.openWebLinkInNewTab(notification);
	}

	private trackInteraction(notification: FeedNotification): void {
		this.actionTrackerService.eventTracker(
			IAction.SCOPE_TYPE.Office,
			IAction.SUBSCOPE_TYPE.Notification,
			IAction.ITEM_TYPE.FeedCard,
			IAction.INTERACTION_TYPE.Click,
			{
				notificationType: notification.actionTracker,
			}
		);
	}

	private openSidebar(notification: FeedNotification): void {
		this.sidebarService.openSidebar(
			{
				mode: ESidebarMode.New,
				date: new Date(),
				contactId: new URLSearchParams(notification.weblink.toString()).get(
					'contactId'
				),
			},
			true
		);
	}

	private openWebLinkInNewTab(notification: FeedNotification): void {
		window.open(notification.weblink, '_blank');
	}

	setNotificationAsHidden(id: number): void {
		const notification = this.notifications.find((n) => n.id === id);

		if (!notification) {
			return;
		}

		this.actionTrackerService.eventTracker(
			IAction.SCOPE_TYPE.Office,
			IAction.SUBSCOPE_TYPE.Notification,
			IAction.ITEM_TYPE.FeedDelete,
			IAction.INTERACTION_TYPE.Click,
			{
				notificationType: notification.actionTracker,
			}
		);

		this.feedNotificationService.setNotificationAsHidden(id).subscribe(
			() => this.removeNotificationFromList(id),
			() => this.showSetNotificationAsHiddenError()
		);
	}

	private removeNotificationFromList(notificationId: number): void {
		this.notifications = this.notifications.filter(
			(n) => n.id !== notificationId
		);
		this.onNotificationHidden.emit(notificationId);
	}

	private showSetNotificationAsHiddenError(): void {
		this.messageService.add({
			id: 'notifications-alert',
			severity: 'error',
			summary: this.translateService.instant('NOTIFICATIONS.CARD.HIDE.TITLE'),
			detail: this.translateService.instant('NOTIFICATIONS.CARD.HIDE.MESSAGE'),
		});
	}

	@HostListener('document:mousedown', ['$event'])
	protected handleClickEvent(event: Event): void {
		this.lockInteraction = this.getNotificationToBeHidden() > 0;

		if (this.eventTargetIsIcon(event.target as HTMLElement)) {
			event.preventDefault();
			return;
		}

		if (this.clickHappendOutsideElement(event)) {
			this.notificationToBeHidden = 0;
		}
	}

	protected formatNotificationDate(createdAt: Date): string {
		if (this.dateTimeService.isToday(createdAt)) {
			return this.translateService.instant('NOTIFICATIONS.CARD.TODAY');
		}

		if (this.dateTimeService.isYesterday(createdAt)) {
			return this.translateService.instant('NOTIFICATIONS.CARD.YESTERDAY');
		}

		if (this.dateTimeService.isSameMonth(createdAt)) {
			return this.showDaysPassedFromCreatedAt(createdAt);
		}

		if (this.dateTimeService.isSameYear(createdAt)) {
			return moment(createdAt).format('DD/MM');
		}

		return moment(createdAt).format('DD/MM/YY');
	}

	private showDaysPassedFromCreatedAt(createdAt: Date): string {
		return `${this.dateTimeService.daysPassedFrom(
			createdAt
		)}${this.translateService.instant('NOTIFICATIONS.CARD.TIME_SYMBOLS.DAYS')}`;
	}

	private eventTargetIsIcon(target: HTMLElement): boolean {
		return target.classList.contains('freud-icon');
	}

	private clickHappendOutsideElement(event: Event): boolean {
		return !this.hideNotificationElement?.nativeElement.contains(event.target);
	}
}
